"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { Section } from "./types";

export function LegalTableOfContents({ sections }: { sections: Section[] }) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "");
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Reset active section whenever the sections list changes (tab switch)
  useEffect(() => {
    setActiveId(sections[0]?.id ?? "");
  }, [sections]);

  useEffect(() => {
    if (sections.length === 0) return;

    const elements = sections
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const visible = new Set<string>();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        // Active = the topmost visible section, in document order.
        const next = sections.find((s) => visible.has(s.id))?.id;
        if (next) setActiveId(next);
      },
      { rootMargin: "-20% 0px -70% 0px" },
    );

    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, [sections]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    history.replaceState(null, "", `#${id}`);
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id);
  };

  const list = (
    <ul className="space-y-1">
      {sections.map((s) => {
        const active = s.id === activeId;
        return (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              onClick={(e) => handleClick(e, s.id)}
              className={cn(
                "block py-1.5 pl-3 -ml-px border-l text-sm transition-colors",
                active
                  ? "border-blue-500 text-blue-600 font-medium"
                  : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300",
              )}
            >
              {s.title}
            </a>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      {/* Mobile: collapsible accordion */}
      <details className="lg:hidden mb-6 rounded-lg border border-gray-200 bg-white">
        <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-gray-900 flex items-center justify-between">
          On this page
          <span className="text-gray-400" aria-hidden>
            ▾
          </span>
        </summary>
        <div className="px-4 pb-4 pt-1">{list}</div>
      </details>

      {/* Desktop: sticky sidebar */}
      <aside className="hidden lg:block lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 pl-3">
          On this page
        </p>
        {list}
      </aside>
    </>
  );
}
